import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EventClickArg } from '@fullcalendar/core';
import { Dossier, Fitter } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import { FitterRouterService } from '@sc/feature-fitter-path-router-system';
import { Resource } from '@sc/feature-fitter-shared-libs';
import { ScreenUtils, dayjs, groupByDateAndSort } from '@sc/utils';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { PlanningPanelsStates } from '../../../models';
import {
  PlanningCalendarService,
  PlanningSandboxService,
} from '../../../services';
import { PlanningCalendarComponent } from '../../planning-calendar';
import { MobilePlanningCalendarComponent } from './mobile-planning-calendar';
import { NgIf, AsyncPipe } from '@angular/common';
import { FitterNameDisplayService } from '@sc/feature-contextual-display';

@Component({
  selector: 'sc-mobile-planning-calendar-container',
  styleUrls: ['./mobile-planning-calendar.container.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<sc-mobile-planning-calendar
      *ngIf="!isLandscapeView"
      [fitter]="fitter"
      [currentDate]="date"
      [groupedDossiers]="groupedDossiers"
      [isLoading]="isLoading$ | async"
      (backClick)="onbackClick()"
      (dossierClick)="ondossierClick($event)"
      (dateChange)="ondateChange($event)"
    >
    </sc-mobile-planning-calendar>
    <sc-planning-calendar
      *ngIf="isLandscapeView"
      [class.isAppointmentFormVisible]="false"
      [class.isAppointmentFormReduced]="false"
      [events]="events$ | async"
      [panelsStates]="planningPanelsStates"
      [resources]="resources"
      [isLandscapeView]="isLandscapeView"
      (handleEventMobileClickEvent)="handleEventClick($event)"
    ></sc-planning-calendar>`,
  imports: [
    NgIf,
    MobilePlanningCalendarComponent,
    PlanningCalendarComponent,
    AsyncPipe,
  ],
  providers: [PlanningSandboxService, FitterNameDisplayService],
})
export class MobilePlanningCalendarContainer implements OnInit {
  fitter: Fitter;
  date: Date;
  resources: Resource[];
  groupedDossiers: Map<string, Dossier[]>;
  isLoading$ = new BehaviorSubject<boolean>(true);
  events$ = this.planningSB.events$;
  isLandscapeView: boolean;
  planningPanelsStates: PlanningPanelsStates = {
    isLeftSideNavOpened: false,
    isRightSideNavOpened: false,
    isAppointmentFormOpened: false,
    isAppointmentFormReduced: false,
    isCustomEventFormOpened: false,
    formErrors: null,
    mustSaveToPrint: false,
  };
  constructor(
    private readonly route: ActivatedRoute,
    private readonly planningSB: PlanningSandboxService,
    public translator: TranslatorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly routerService: FitterRouterService,
    public planningCalendarService: PlanningCalendarService,
    private readonly authService: AuthService
  ) {}

  ngOnInit() {
    this.date = new Date();
    this.isLandscapeView = ScreenUtils.isLandScapeResolution();
    this.isLoading$.next(true);

    this.planningSB.initPlanning();
    this.authService.isAuthenticated$().subscribe((isauth) => {
      this.planningSB.setIsAuth(isauth);
    });

    this.route.params.subscribe((params) => {
      if (params?.fitterId) {
        this.planningSB
          .fetchFitters()
          .pipe(take(1))
          .subscribe(
            (fitters) => {
              this.fitter = fitters.find(
                (fitter) => fitter.id === params?.fitterId
              );
              this.fetchSlotsByDates(this.date);
              this.planningSB.resources$.subscribe((res) => {
                this.resources = res.filter(
                  (r) => r.fitterId === this.fitter.id
                );
                this.changeDetectorRef.detectChanges();
              });
            },
            (error) => {
              console.log(error);
              this.isLoading$.next(false);
            }
          );
      }
    });
  }

  fetchSlotsByDates(date: Date | string) {
    this.isLoading$.next(true);
    const begin = dayjs(date).startOf('month').format('YYYY-MM-DD hh:mm');
    const end = dayjs(date).endOf('month').format('YYYY-MM-DD hh:mm');
    this.planningSB
      .fetchDossiersByFitters(this.fitter.id, begin, end)
      .subscribe(
        (dossiers) => {
          const filteredDossiers = dossiers?.filter(
            (dossier) =>
              dossier.state === 'COMPLETE' &&
              dossier?.appointment?.isValidatedByFitter
          );
          this.groupedDossiers = groupByDateAndSort(
            filteredDossiers as Dossier[]
          );
          this.isLoading$.next(false);
          this.changeDetectorRef.detectChanges();
        },
        (error) => {
          console.log(error);
          this.isLoading$.next(false);
        }
      );
  }

  ondateChange(date) {
    this.date = new Date(date);
    this.fetchSlotsByDates(this.date);
  }

  onbackClick() {
    this.routerService.goToPlanning();
  }

  ondossierClick(dossierId: string) {
    this.routerService.goToMobileDossier(dossierId);
  }

  @HostListener('window:resize', ['$event'])
  redirectToFitterPageIfDesktopResolution(): void {
    this.isLandscapeView = ScreenUtils.isLandScapeResolution();
  }

  handleEventClick(infos: EventClickArg) {
    const dossierId = infos?.event?.extendedProps?.dossier?.id;
    this.ondossierClick(dossierId);
  }
}
